import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Camera, CameraOff, CheckCircle, XCircle, ScanLine,
  Building2, RotateCcw, Loader2, Keyboard,
} from 'lucide-react';
import { barcodeAPI, establishmentAPI } from '../api/apiService';
import { useAuth, useActiveEstId } from './AuthContext';
import './AttendanceScannerKiosk.css';

const STATE = { IDLE: 'idle', SUCCESS: 'success', ERROR: 'error' };

export default function AttendanceScannerKiosk() {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [establishments, setEstablishments] = useState([]);
  const [selectedEst, setSelectedEst]       = useState(lockedEstId || '');
  const [manualBarcode, setManualBarcode]   = useState('');
  const [processing, setProcessing]         = useState(false);
  const [scanState, setScanState]           = useState(STATE.IDLE);
  const [result, setResult]                 = useState(null);
  const [lastScan, setLastScan]             = useState(null);
  const [toast, setToast]                   = useState(null);
  const [cameraOn, setCameraOn]             = useState(false);
  const [cameraError, setCameraError]       = useState('');

  const inputRef    = useRef(null);
  const scannerRef  = useRef(null);
  const cooldownRef = useRef(false);

  // Load establishments and auto-focus input
  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setSelectedEst(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!lockedEstId) {
      establishmentAPI.getAll()
        .then(res => {
          const ests = res.establishments || [];
          setEstablishments(ests);
          if (ests.length === 1) setSelectedEst(String(ests[0].id));
        })
        .catch(() => {});
    }
    if (inputRef.current) inputRef.current.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (msg, success) => {
    setToast({ msg, success });
    setTimeout(() => setToast(null), 3500);
  };

  const playBeep = (success) => {
    try {
      const ctx  = new (window.AudioContext || window.webkitAudioContext)();
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = success ? 880 : 440;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.start();
      osc.stop(ctx.currentTime + 0.4);
    } catch (_) {}
  };

  const processBarcode = useCallback(async (barcode) => {
    if (cooldownRef.current || !barcode.trim()) return;
    cooldownRef.current = true;
    setProcessing(true);
    setResult(null);
    setScanState(STATE.IDLE);

    try {
      const data = await barcodeAPI.scan(barcode.trim(), selectedEst ? Number(selectedEst) : null);
      setResult(data);
      setScanState(data.success ? STATE.SUCCESS : STATE.ERROR);
      showToast(data.message, data.success);
      playBeep(data.success);
      if (data.success) {
        setLastScan({
          employee_name: data.employee_name,
          scan_type: data.scan_type,
          ts: new Date(),
        });
      }
    } catch (_) {
      const errData = { success: false, message: 'Network error. Please try again.' };
      setResult(errData);
      setScanState(STATE.ERROR);
      showToast(errData.message, false);
      playBeep(false);
    } finally {
      setProcessing(false);
      setManualBarcode('');
      setTimeout(() => {
        cooldownRef.current = false;
        if (inputRef.current) inputRef.current.focus();
      }, 2200);
    }
  }, [selectedEst]);

  // Physical scanner sends Enter after barcode
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      processBarcode(manualBarcode);
    }
  };

  // Camera scanner
  const startCamera = async () => {
    setCameraError('');
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const qr = new Html5Qrcode('ks-qr-reader');
      scannerRef.current = qr;
      await qr.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decoded) => processBarcode(decoded),
        () => {},
      );
      setCameraOn(true);
    } catch (_) {
      setCameraError('Camera access denied or unavailable.');
      scannerRef.current = null;
    }
  };

  const stopCamera = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (_) {}
      scannerRef.current = null;
    }
    setCameraOn(false);
  };

  useEffect(() => () => { stopCamera(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="ks-page">

      {/* Toast notification */}
      {toast && (
        <div className={`ks-toast ${toast.success ? 'ks-toast-ok' : 'ks-toast-err'}`}>
          {toast.success ? <CheckCircle size={15} /> : <XCircle size={15} />}
          <span>{toast.msg}</span>
        </div>
      )}

      {/* Page header */}
      <div className="ks-header">
        <div className="ks-header-icon"><ScanLine size={20} /></div>
        <div>
          <h1 className="ks-title">Barcode Scanner</h1>
          <p className="ks-subtitle">Scan employee badges to record shift check-in / check-out</p>
        </div>
      </div>

      {/* Establishment selector — hidden for ClientAdmin */}
      {!lockedEstId && (
      <div className="ks-est-bar">
        <Building2 size={15} className="ks-est-icon" />
        <label className="ks-est-label">Organization:</label>
        <select
          className="ks-est-select"
          value={selectedEst}
          onChange={e => setSelectedEst(e.target.value)}
        >
          <option value="">All Organizations</option>
          {establishments.map(e => (
            <option key={e.id} value={String(e.id)}>{e.name}</option>
          ))}
        </select>
      </div>
      )}

      <div className="ks-grid">

        {/* ── Left column: input + camera ── */}
        <div className="ks-left">

          {/* Physical / manual barcode input */}
          <div className="ks-card">
            <h2 className="ks-card-title">
              <Keyboard size={15} /> Scanner Input
            </h2>
            <p className="ks-hint">
              Point a physical barcode reader at a badge — or type the ID and press Enter
            </p>
            <div className="ks-input-row">
              <input
                ref={inputRef}
                className="ks-input"
                placeholder="Scan or type barcode…"
                value={manualBarcode}
                onChange={e => setManualBarcode(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                disabled={processing}
                autoComplete="off"
                spellCheck={false}
              />
              <button
                className="ks-scan-btn"
                onClick={() => processBarcode(manualBarcode)}
                disabled={processing || !manualBarcode.trim()}
              >
                {processing ? <Loader2 size={15} className="ks-spin" /> : 'Scan'}
              </button>
            </div>

            {/* Scan result banner */}
            {result && scanState !== STATE.IDLE && (
              <div className={`ks-result ${scanState === STATE.SUCCESS ? 'ks-result-ok' : 'ks-result-err'}`}>
                <span className="ks-result-icon">
                  {scanState === STATE.SUCCESS
                    ? <CheckCircle size={17} />
                    : <XCircle size={17} />}
                </span>
                <span className="ks-result-msg">{result.message}</span>
                <button
                  className="ks-clear-btn"
                  title="Clear"
                  onClick={() => { setResult(null); setScanState(STATE.IDLE); }}
                >
                  <RotateCcw size={13} />
                </button>
              </div>
            )}
          </div>

          {/* Camera scanner (QR code via phone camera) */}
          <div className="ks-card">
            <div className="ks-card-head">
              <h2 className="ks-card-title"><Camera size={15} /> Camera Scanner</h2>
              <button
                className={cameraOn ? 'ks-cam-stop' : 'ks-cam-start'}
                onClick={cameraOn ? stopCamera : startCamera}
              >
                {cameraOn
                  ? <><CameraOff size={13} /> Stop</>
                  : <><Camera size={13} /> Start</>}
              </button>
            </div>

            <style>{`
              #ks-qr-reader img { display:none !important; }
              #ks-qr-reader > div:last-child { display:none !important; }
              #ks-qr-reader video { width:100% !important; height:100% !important; object-fit:cover; border-radius:10px; }
              #ks-qr-reader { border:none !important; padding:0 !important; }
            `}</style>

            <div className="ks-cam-box">
              <div
                id="ks-qr-reader"
                style={{ display: cameraOn ? 'block' : 'none', width: '100%', height: '100%' }}
              />
              {cameraOn && (
                <div className="ks-viewfinder" aria-hidden="true">
                  <div className="ks-corner ks-corner-tl" />
                  <div className="ks-corner ks-corner-tr" />
                  <div className="ks-corner ks-corner-bl" />
                  <div className="ks-corner ks-corner-br" />
                </div>
              )}
              {!cameraOn && (
                <div className="ks-cam-placeholder">
                  <Camera size={34} />
                  <p>{cameraError || 'Click Start to enable camera QR scanning'}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Right column: last scanned ── */}
        <div className="ks-right">
          <div className="ks-card ks-last-card">
            <h2 className="ks-card-title">Last Scanned</h2>

            {!lastScan ? (
              <div className="ks-empty-scan">
                <div className="ks-emp-avatar-placeholder">
                  <ScanLine size={26} />
                </div>
                <p>Scan a badge to see details here</p>
              </div>
            ) : (
              <div className="ks-last-content">
                <div className={`ks-emp-avatar ${lastScan.scan_type === 'CHECKIN' ? 'ks-av-in' : 'ks-av-out'}`}>
                  {(lastScan.employee_name || '?')[0].toUpperCase()}
                </div>
                <p className="ks-emp-name">{lastScan.employee_name}</p>
                <span className={`ks-badge ${lastScan.scan_type === 'CHECKIN' ? 'ks-badge-in' : 'ks-badge-out'}`}>
                  {lastScan.scan_type === 'CHECKIN' ? '✓ Checked In' : '→ Checked Out'}
                </span>
                <p className="ks-scan-time">{lastScan.ts.toLocaleTimeString()}</p>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
